import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { ValidationType } from 'enums/validationType';
import { SelectListItemModel, sortSelectList } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormValidationService } from 'services/form-validation.service';

export interface IAddedItem {
	value: string;
	label: string;
}

@Component({
	selector: 'add-multiple',
	templateUrl: './add-multiple.component.html',
	imports: [MatFormField, MatLabel, MatChipGrid, MatChipRow, MatIcon, MatChipRemove, InputStateDirective, MatInput, FormsModule, ValidationDirective, MatAutocompleteTrigger, MatChipInput, AutocompleteOffDirective, MatAutocomplete, MatOption, MatError, MatHint, TranslatePipe]
})
export class AddMultipleComponent implements AfterViewInit, OnDestroy {
	@Output() itemAdded = new EventEmitter<string>();

	@ViewChild('input') input!: ElementRef<HTMLInputElement>;
	@ViewChild('chipList') matChipList!: MatChipGrid;
	@ViewChild('auto') matAutoComplete?: MatAutocomplete;
	@ViewChild(NgModel) ngModel!: NgModel;

	@Input() emitNewValues = false;
	@Input() validation?: ValidationType;
	@Input() charactersForSuggestions = 0;
	private _required = false;
	@Input() set required(value: boolean | undefined) {
		this._required = value ?? false;
		this.validate();
	}
	get required() { return this._required; }
	@Input() label = '';
	@Input() placeholder = '';

	private _selectedItems: IAddedItem[] = [];
	@Input()
	set selectedItems(values: IAddedItem[]) {
		this._selectedItems = values;
	}
	get selectedItems() {
		return this._selectedItems;
	}
	@Output() selectedItemsChange = new EventEmitter<IAddedItem[]>();

	private _items: IAddedItem[] = [];
	@Input()
	set availableItems(values: IAddedItem[]) {
		this._items = values;
		this.findUnusedItems();
	}
	get availableItems() {
		return this._items;
	}
	get selectList() {
		return this._items.map(item => new SelectListItemModel(item.value, item.label));
	}

	pristine = true;
	separatorKeysCodes: number[] = [ENTER, COMMA];
	inputValue = '';
	inputFocused = false;
	validationMessage = '';
	unusedItems: SelectListItemModel[] = [];
	filteredUnusedItems: SelectListItemModel[] = [];

	constructor(@Optional() private readonly formValidation?: FormValidationService) { }

	validate() {
		this.formValidation?.fieldStateChanged(this, !this.required || this.selectedItems.length > 0, this.pristine);
	}

	markAsPristine() { this.pristine = true; }

	ngAfterViewInit() {
		this.matAutoComplete!.autoActiveFirstOption = !this.emitNewValues;
	}
	ngOnDestroy() {
		this.formValidation?.removeField(this);
	}

	showOptions(): boolean {
		if (this.inputValue.length >= this.charactersForSuggestions) return true;
		return false;
	}

	findUnusedItems() {
		const values = new Set(this.selectedItems.map(t => t.value));
		this.unusedItems = this.selectList.filter(item => !values.has(item.value as string));
		sortSelectList(this.unusedItems);
		this.filterSelectList();
	}

	filterSelectList(value = '') {
		if (typeof value !== 'string') return;
		this.filteredUnusedItems = this.unusedItems.filter(a => ~a.name.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()));
	}

	removeValue(item: IAddedItem) {
		this.removeItem(this.selectedItems.indexOf(item));
	}

	tryAddValue(input: string) {
		if (!this.emitNewValues) return;
		this.addValue(input);
	}

	addValue(input: string) {
		input = input.trim().replace(/\s+/g, ' ');
		if (!input) return;

		const foundItem = this.unusedItems.find(x => x.name === input);
		if (foundItem) input = foundItem.value as string;

		if (this.validation === ValidationType.Email && !input.isValidEmailAddress()) {
			this.validationMessage = ValidationType[ValidationType.Email];
			this.matChipList.errorState = true;
			return;
		}
		if (this.validation === ValidationType.Domain && !input.isValidDomain()) {
			this.validationMessage = ValidationType[ValidationType.Domain];
			this.matChipList.errorState = true;
			return;
		}
		this.matChipList.errorState = false;
		let addItem = foundItem ? <IAddedItem>{ label: foundItem.name, value: foundItem.value } : <IAddedItem>{ label: input, value: input };

		this.pristine = false;
		this.selectedItems.push(addItem);
		this.itemAdded.emit(addItem.value);
		this.resetInput();
	}

	resetInput(blur: boolean = false) {
		this.input.nativeElement.value = '';
		this.ngModel.control.setValue('');
		this.ngModel.control.markAsPristine();
		if (blur) this.matChipList.errorState = false;
		this.updateSelectedList();
	}

	removeItem(index: number) {
		this.pristine = false;
		this.selectedItems.splice(index, 1);
		this.updateSelectedList();
	}

	updateSelectedList() {
		this.findUnusedItems();
		this.selectedItemsChange.emit(this.selectedItems);
		this.validate();
	}
}
